// Maintains the live "watch" state for a persistent-agent session from its SSE
// frames: the dashboard (a Map of fact lanes, upserted in place), the doorbell
// (an append-only event feed, newest first), the transcript (raw chunks), and
// the current status. Mirrors the host → console contract
// (PersistentAgentWatchFrame); see apps/persistent-agent-host/src/watch.ts.
import type {
  PersistentAgentEventKind,
  PersistentAgentFactType,
  PersistentAgentStatus,
  PersistentAgentStatusColor,
  PersistentAgentTranscriptChunk,
  PersistentAgentTurn,
} from '@cortex/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthProvider';

import { subscribePersistentAgentWatch } from './sse';

export interface IWatchFact {
  laneId: string;
  factType: PersistentAgentFactType;
  summary: string;
  data: unknown;
  date: string;
}

export interface IWatchEvent {
  eventKind: PersistentAgentEventKind;
  content: string;
  source: string;
  wokeFromIdle: boolean;
  /** True only when an `interrupt` delivery actually aborted an in-flight run. */
  interrupted?: boolean;
  /** Present only on incident-attach events — the attached workflow run id.
   *  Lets the chat rebuild inline incident cards from the durable event feed. */
  runId?: string;
  at: string;
}

/** The agent's self-reported "what I'm working on" line (its set_status tool). */
export interface IWatchActivity {
  text: string;
  at: string;
}

/** The agent's self-reported health tint + why (its set_status_color tool). */
export interface IWatchStatusColor {
  color: PersistentAgentStatusColor;
  reason: string;
  at: string;
}

export interface IWatchState {
  status: PersistentAgentStatus | null;
  /** Latest self-reported activity line; null until the agent sets one. */
  activity: IWatchActivity | null;
  /** Latest self-reported health tint; null until the agent sets one. */
  statusColor: IWatchStatusColor | null;
  facts: Record<string, IWatchFact>;
  events: IWatchEvent[];
  transcript: PersistentAgentTranscriptChunk[];
  /** Live turns upserted by id as they start/finalize; merged with the `/turns` REST backfill by the panels. */
  turns: Record<string, PersistentAgentTurn>;
}

type Frame =
  | ({ kind: 'fact' } & IWatchFact)
  | ({ kind: 'event' } & IWatchEvent)
  | { kind: 'status'; status: PersistentAgentStatus; at: string }
  | ({ kind: 'activity' } & IWatchActivity)
  | ({ kind: 'status_color' } & IWatchStatusColor)
  | { kind: 'chunk'; part: PersistentAgentTranscriptChunk }
  | { kind: 'turn'; turn: PersistentAgentTurn; at: string };

/**
 * Internal reducer action: a wire frame, a reset before a reconnect replay, a
 * coalesced batch of chunk frames (the hook buffers fast token streams and
 * flushes them as one action so the transcript — and thus the whole chat
 * timeline + markdown parse — updates at most once per flush, not per token),
 * or a full reconnect replay applied atomically (see `replay` below).
 */
type WatchAction =
  | Frame
  | { kind: 'chunk-batch'; parts: PersistentAgentTranscriptChunk[] }
  | { kind: 'clear-history' }
  | { kind: 'replay'; frames: Frame[] };

const eventCap = 200;
/**
 * How long the stream must stay down before the "Reconnecting…" banner shows.
 * EventSource fires `error` on every transient blip (even ones the browser
 * recovers from natively within a few hundred ms), so surfacing the banner
 * immediately makes it flash on/off. Debouncing to this window means only a
 * genuine outage paints the banner; brief hiccups reconnect invisibly.
 */
const reconnectBannerDelayMs = 1200;
/**
 * Every (re)connect replays full history as a burst of individual frames.
 * Dispatching each one as it arrives — after wiping state for the reset —
 * visibly empties the chat and regrows it frame by frame ("thrashing").
 * Instead the hook buffers a reconnect's frames and applies them as ONE
 * `replay` action once the burst goes quiet for this long, so the swap from
 * old (stale) state to new (identical, since replay reproduces the same
 * history) state is a single atomic render — invisible to the user.
 */
const replaySettleGapMs = 200;
/**
 * Hard cap on how long to buffer a replay before forcing the swap, so a
 * reconnect that lands mid an actively-streaming response doesn't leave the
 * chat looking stale/stuck for too long while frames keep trickling in.
 */
const replayMaxWaitMs = 2000;

const initialState: IWatchState = {
  status: null,
  activity: null,
  statusColor: null,
  facts: {},
  events: [],
  transcript: [],
  turns: {},
};

export function reducer(state: IWatchState, frame: WatchAction): IWatchState {
  switch (frame.kind) {
    case 'clear-history':
      return { ...state, transcript: [], events: [], turns: {} };
    case 'fact':
      // Upsert the lane in place — the dashboard, deduped by laneId.
      return {
        ...state,
        facts: {
          ...state.facts,
          [frame.laneId]: {
            laneId: frame.laneId,
            factType: frame.factType,
            summary: frame.summary,
            data: frame.data,
            date: frame.date,
          },
        },
      };
    case 'event':
      // The doorbell — newest first, capped.
      return {
        ...state,
        events: [
          {
            eventKind: frame.eventKind,
            content: frame.content,
            source: frame.source,
            wokeFromIdle: frame.wokeFromIdle,
            interrupted: frame.interrupted,
            runId: frame.runId,
            at: frame.at,
          },
          ...state.events,
        ].slice(0, eventCap),
      };
    case 'status':
      return { ...state, status: frame.status };
    case 'activity':
      // Latest line wins — replaced in place, like status.
      return { ...state, activity: { text: frame.text, at: frame.at } };
    case 'status_color':
      // Latest tint wins — the shift HISTORY lives in the durable color log
      // (`GET :id/agents/:agentId/status-colors`), not in this live state.
      return {
        ...state,
        statusColor: { color: frame.color, reason: frame.reason, at: frame.at },
      };
    case 'chunk':
      // Full history, uncapped — the console always shows the complete
      // transcript, including the whole current turn while it's still
      // streaming. Render cost is bounded separately by AgentChat's DOM
      // window (only the newest ~80 items mount; older ones sit behind
      // "Show earlier").
      return { ...state, transcript: [...state.transcript, frame.part] };
    case 'chunk-batch':
      // Coalesced fast token stream — append all buffered parts in one pass.
      return { ...state, transcript: [...state.transcript, ...frame.parts] };
    case 'turn':
      // Upsert by turn id — one 'running' snapshot at start, one full-metrics
      // frame at finalize; the second overwrites the first in place.
      return { ...state, turns: { ...state.turns, [frame.turn.id]: frame.turn } };
    case 'replay':
      // Fold the whole buffered reconnect replay onto a fresh state in one
      // pure computation, then swap it in as a single dispatch — the reset
      // and every replayed frame land in the same render.
      return frame.frames.reduce(reducer, initialState);
    default:
      return state;
  }
}

/** Live watch state plus the SSE connection health for a reconnect banner. */
export interface IWatchResult extends IWatchState {
  connection: 'live' | 'reconnecting';
  /**
   * False until this thread's initial full-history replay has landed. Lets the
   * view show a loading placeholder instead of flashing the "empty thread"
   * state (or the previous thread's transcript) in the ~200 ms replay-settle
   * window after opening/switching a thread.
   */
  hasLoaded: boolean;
  /** Optimistically remove pre-reset conversation history from the open view. */
  clearHistory: () => void;
}

/**
 * Open one SSE subscription for the session while mounted; rebuild state from
 * the replayed frames + live tail. Returns the derived watch state plus the
 * connection health (so the UI can show a reconnecting banner). The stream
 * never closes on error — it reconnects and replays. The reducer state
 * OUTLIVES any one EventSource (the effect resubscribes on token rotation), so
 * `onReconnect` fires on every open — same-socket reconnect or fresh-socket
 * resubscribe — and every frame from that connection's replay is buffered and
 * applied as one atomic `replay` swap (see replaySettleGapMs); otherwise the
 * replay would both duplicate the chat and visibly empty-then-regrow it.
 */
export function usePersistentAgentWatch(groupId: string, agentId: string): IWatchResult {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  // Hold the token in a ref read at (re)connect time instead of a hook dep.
  // Keycloak rotates the token on a short TTL; if it were an effect dep, every
  // rotation would tear down the EventSource and trigger a reset + full-history
  // replay — the visible "dump" of the whole thread. The SSE layer reads this
  // getter when it (re)connects, so a rotation is a no-op for the live stream
  // and a genuine reconnect still uses a fresh token.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const [state, dispatch] = useReducer(reducer, initialState);
  // Optimistic 'live' so there's no banner flash on first mount; flips on error.
  const [connection, setConnection] = useState<'live' | 'reconnecting'>('live');
  // False until this thread's first replay lands (see flushReplay). Reset on
  // every thread switch (the effect below re-runs on agentId change) so the
  // view shows a loading placeholder, not a stale/empty flash, until the new
  // thread's history arrives.
  const [hasLoaded, setHasLoaded] = useState(false);

  // Coalesce fast token streams. Each streamed token arrives as its own `chunk`
  // frame; dispatching per token re-renders the whole chat (buildTimeline +
  // markdown re-parse) many times a second and flickers. Buffer chunks and
  // flush them as ONE `chunk-batch` every flushMs; any non-chunk frame flushes
  // the buffer first so ordering (e.g. chunk → turn-finalize) is preserved.
  // This only applies to the LIVE tail — a reconnect's replay burst is
  // buffered separately below and applied as one atomic `replay`.
  const pendingChunks = useRef<PersistentAgentTranscriptChunk[]>([]);
  const flushTimer = useRef<number | null>(null);
  // Reconnect replay buffering: every (re)open replays full history as a
  // burst of frames. Dispatching each one individually — after wiping state
  // for the reset — visibly empties the chat and regrows it frame by frame.
  // Instead every frame that arrives while `replaying` is true is buffered
  // here and applied as a single `replay` action once the burst settles (see
  // replaySettleGapMs / replayMaxWaitMs), so old and new state swap in one
  // render — invisible, since replay reproduces the same history.
  const replaying = useRef(false);
  const replayBuffer = useRef<Frame[]>([]);
  const replaySettleTimer = useRef<number | null>(null);
  const replayMaxTimer = useRef<number | null>(null);
  // Debounce the "Reconnecting…" banner (see reconnectBannerDelayMs). A 'live'
  // signal cancels a pending banner immediately; a 'reconnecting' signal only
  // paints after the delay, so transient blips that recover first never flash.
  const reconnectDelayTimer = useRef<number | null>(null);

  useEffect(() => {
    // Thread switch (agentId changed — token rotation does NOT re-run this
    // effect): clear the previous thread's state now and mark unloaded, so the
    // view never shows the old transcript or the "empty" state before this
    // thread's replay lands. `replay` with no frames folds onto a fresh state.
    dispatch({ kind: 'replay', frames: [] });
    setHasLoaded(false);
    const flushMs = 80;
    const flush = () => {
      if (flushTimer.current !== null) {
        window.clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
      if (pendingChunks.current.length > 0) {
        const parts = pendingChunks.current;
        pendingChunks.current = [];
        dispatch({ kind: 'chunk-batch', parts });
      }
    };
    const flushReplay = () => {
      if (!replaying.current) return;
      replaying.current = false;
      if (replaySettleTimer.current !== null) {
        window.clearTimeout(replaySettleTimer.current);
        replaySettleTimer.current = null;
      }
      if (replayMaxTimer.current !== null) {
        window.clearTimeout(replayMaxTimer.current);
        replayMaxTimer.current = null;
      }
      const frames = replayBuffer.current;
      replayBuffer.current = [];
      dispatch({ kind: 'replay', frames });
      // The thread's history (however much there is — a status frame at
      // minimum) has now landed; safe to reveal the transcript or the genuine
      // empty state.
      setHasLoaded(true);
    };
    const setConn = (next: 'live' | 'reconnecting') => {
      if (next === 'live') {
        if (reconnectDelayTimer.current !== null) {
          window.clearTimeout(reconnectDelayTimer.current);
          reconnectDelayTimer.current = null;
        }
        setConnection('live');
        return;
      }
      // 'reconnecting' is deferred; a later 'live' within the window cancels it.
      if (reconnectDelayTimer.current !== null) return;
      reconnectDelayTimer.current = window.setTimeout(() => {
        reconnectDelayTimer.current = null;
        setConnection('reconnecting');
      }, reconnectBannerDelayMs);
    };
    const baseUrl = (
      import.meta.env.VITE_API_URL ?? 'https://api.cortex-dev.formant.ai/api'
    ).replace(/\/$/, '');
    const stop = subscribePersistentAgentWatch(
      baseUrl,
      groupId,
      agentId,
      () => tokenRef.current,
      (frame) => {
        const f = frame as Frame;
        if (replaying.current) {
          replayBuffer.current.push(f);
          if (replaySettleTimer.current !== null) {
            window.clearTimeout(replaySettleTimer.current);
          }
          replaySettleTimer.current = window.setTimeout(flushReplay, replaySettleGapMs);
          return;
        }
        if (f.kind === 'chunk') {
          // Live tail only. A replay of old bot_create results must not refetch.
          if (
            f.part.type === 'tool-result' &&
            !f.part.isError &&
            (f.part.name === 'bot_create' || f.part.name === 'bot_update')
          ) {
            queryClientRef.current
              .invalidateQueries({ queryKey: ['formant-bots'] })
              .catch((err) => {
                console.error(err);
              });
          }
          pendingChunks.current.push(f.part);
          if (flushTimer.current === null) {
            flushTimer.current = window.setTimeout(flush, flushMs);
          }
          return;
        }
        // Non-chunk frame: drain buffered chunks first so the transcript stays
        // in wire order, then apply this frame immediately.
        flush();
        dispatch(f);
      },
      {
        onConnectionChange: setConn,
        onReconnect: () => {
          pendingChunks.current = [];
          if (flushTimer.current !== null) {
            clearTimeout(flushTimer.current);
            flushTimer.current = null;
          }
          // Start buffering this connection's replay burst instead of
          // dispatching it frame by frame.
          replaying.current = true;
          replayBuffer.current = [];
          if (replaySettleTimer.current !== null) window.clearTimeout(replaySettleTimer.current);
          replaySettleTimer.current = window.setTimeout(flushReplay, replaySettleGapMs);
          if (replayMaxTimer.current !== null) window.clearTimeout(replayMaxTimer.current);
          replayMaxTimer.current = window.setTimeout(flushReplay, replayMaxWaitMs);
        },
      },
    );
    return () => {
      flush();
      stop();
      if (reconnectDelayTimer.current !== null) {
        window.clearTimeout(reconnectDelayTimer.current);
        reconnectDelayTimer.current = null;
      }
      if (replaySettleTimer.current !== null) {
        window.clearTimeout(replaySettleTimer.current);
        replaySettleTimer.current = null;
      }
      if (replayMaxTimer.current !== null) {
        window.clearTimeout(replayMaxTimer.current);
        replayMaxTimer.current = null;
      }
    };
    // token intentionally omitted — read via tokenRef so a rotation doesn't
    // resubscribe (which would reset + replay the whole thread).
  }, [groupId, agentId]);

  const clearHistory = useCallback(() => {
    pendingChunks.current = [];
    replayBuffer.current = [];
    dispatch({ kind: 'clear-history' });
  }, []);

  return { ...state, connection, hasLoaded, clearHistory };
}

/**
 * Merge the `/turns` REST backfill with the live SSE turns, deduped by id
 * (a live turn always wins over its REST snapshot, since the REST poll can
 * be up to 15s stale), sorted newest-first by `startedAt`. Shared by
 * MetricsPanel and TracePanel so both tabs read the exact same live+history
 * timeline.
 */
export function mergeTurns(
  restTurns: PersistentAgentTurn[],
  liveTurns: PersistentAgentTurn[] | undefined,
): PersistentAgentTurn[] {
  const byId = new Map<string, PersistentAgentTurn>();
  for (const turn of restTurns) byId.set(turn.id, turn);
  for (const turn of liveTurns ?? []) byId.set(turn.id, turn);
  return [...byId.values()].sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
  );
}
